/**
 * Цвета графиков дизайн-системы. Правила — навык dataviz (references/color-formula.md, palette.md):
 *
 * - категориальная палитра — 8 слотов в фиксированном порядке, цвет закреплён за сущностью, а не за местом
 *   в рейтинге; девятый ряд не получает новый цвет, а уходит в «Прочее» (`OTHER`);
 * - последовательная шкала — один оттенок (синий), от светлого к тёмному; в тёмной теме «ноль» уходит к фону;
 * - расходящаяся — два противоположных полюса (красный ↔ синий) и серая середина «нет изменения»;
 * - порядковая (шаги воронки, фазы) — один оттенок с монотонной светлотой;
 * - статусные — только для статусов и только вместе с иконкой и подписью.
 *
 * Значения взяты из проверенной палитры навыка и прогнаны валидатором `validate_palette.js` на поверхностях
 * дизайн-системы — белой карточке (`#ffffff`) и `zinc-900` (`#18181b`). Результаты — в комментариях ниже.
 * Подписи, значения и легенды красятся цветом текста DS, никогда цветом ряда.
 */

export type ChartMode = 'light' | 'dark'

/**
 * Категориальная палитра, светлая тема. Валидатор (adjacent, surface #ffffff): все проверки PASS;
 * худшая соседняя пара CVD ΔE 9.1, обычное зрение ΔE 19.6. Контраст ниже 3:1 у слотов 3, 4, 5 (WARN) —
 * поэтому у каждого графика есть табличный вид и подписи значений.
 */
export const CATEGORICAL_LIGHT = [
  '#2a78d6', // 1 синий
  '#eb6834', // 2 оранжевый
  '#1baf7a', // 3 бирюзовый
  '#eda100', // 4 жёлтый
  '#e87ba4', // 5 пурпурный
  '#008300', // 6 зелёный
  '#6250d6', // 7 фиолетовый
  '#e34948', // 8 красный
] as const

/** Тёмная тема — те же оттенки, свои ступени. Валидатор (surface #18181b): все PASS, CVD ΔE 8.4, обычное 19.3. */
export const CATEGORICAL_DARK = [
  '#3987e5',
  '#d95926',
  '#199e70',
  '#c98500',
  '#d55181',
  '#008300',
  '#9085e9',
  '#e66767',
] as const

/** Номер слота (0..7) за постоянными сущностями. Пара/тройка площадок проверена валидатором `--pairs all`. */
export const ENTITY_SLOTS = {
  marketplace: {
    ozon: 0, // синий
    yandex_market: 3, // жёлтый
    wb: 4, // пурпурный
  } as Record<string, number>,
} as const

/** Последовательная шкала (синий), от «мало» к «много». Ступени 100…700 палитры навыка. */
export const SEQUENTIAL_LIGHT = [
  '#cde2fb',
  '#b7d3f6',
  '#9ec5f4',
  '#86b6ef',
  '#6da7ec',
  '#5598e7',
  '#3987e5',
  '#2a78d6',
  '#256abf',
  '#1c5cab',
  '#184f95',
  '#104281',
  '#0d366b',
] as const

/** В тёмной теме якорь переворачивается: «мало» темнее и уходит к фону, «много» светлее. */
export const SEQUENTIAL_DARK = [
  '#0d366b',
  '#104281',
  '#184f95',
  '#1c5cab',
  '#256abf',
  '#2a78d6',
  '#3987e5',
  '#5598e7',
  '#6da7ec',
  '#86b6ef',
  '#9ec5f4',
  '#b7d3f6',
] as const

/** Расходящаяся шкала: [минус, середина, плюс]. Минус — красный, плюс — синий, середина — серая. */
export const DIVERGING_LIGHT = ['#e34948', '#f0efec', '#2a78d6'] as const
export const DIVERGING_DARK = ['#e66767', '#383835', '#3987e5'] as const

/**
 * Порядковая шкала для шагов воронки и фаз, от первого шага к последнему.
 * Валидатор `--ordinal`: светлая (surface #ffffff) — PASS, светлый конец 2.11:1;
 * тёмная (surface #18181b) — PASS, тёмный конец 2.19:1.
 */
export const ORDINAL_LIGHT = ['#104281', '#1c5cab', '#2a78d6', '#5598e7', '#86b6ef'] as const
export const ORDINAL_DARK = ['#b7d3f6', '#86b6ef', '#5598e7', '#2a78d6', '#184f95'] as const

/** Статусы. Не тематизируются и никогда не красят «обычный» ряд; показываются только с иконкой и подписью. */
export const STATUS_COLORS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
} as const

export type StatusTone = keyof typeof STATUS_COLORS

/** Токены графика для одной темы. Строятся один раз на тему (`getChartTheme`). */
export interface ChartTheme {
  mode: ChartMode
  /** Поверхность карточки: зазоры между метками и кольца точек рисуются её цветом. */
  surface: string
  /** Основной текст (значения). */
  text: string
  /** Вторичный текст (подписи, легенда). */
  textSecondary: string
  /** Приглушённый текст (оси). */
  textMuted: string
  /** Линии сетки — тонкие, сплошные, на шаг от поверхности. */
  grid: string
  /** Базовая линия и оси. */
  axis: string
  /** Фон пустых ячеек календаря и дорожек. */
  track: string
  tooltipBackground: string
  tooltipBorder: string
  categorical: readonly string[]
  /** «Прочее» и приглушённые ряды. */
  other: string
  sequential: readonly string[]
  diverging: readonly [string, string, string]
  ordinal: readonly string[]
  /** Цвет текста роста и падения (Δ). */
  positiveText: string
  negativeText: string
  status: typeof STATUS_COLORS
  fontFamily: string
}

const FONT_FAMILY = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif'

export const LIGHT_THEME: ChartTheme = {
  mode: 'light',
  surface: '#ffffff',
  text: '#111827', // gray-900
  textSecondary: '#4b5563', // gray-600
  textMuted: '#6b7280', // gray-500
  grid: '#e5e7eb', // gray-200
  axis: '#d1d5db', // gray-300
  track: '#f3f4f6', // gray-100
  tooltipBackground: '#ffffff',
  tooltipBorder: '#e5e7eb',
  categorical: CATEGORICAL_LIGHT,
  other: '#9ca3af', // gray-400
  sequential: SEQUENTIAL_LIGHT,
  diverging: DIVERGING_LIGHT,
  ordinal: ORDINAL_LIGHT,
  positiveText: '#16a34a', // green-600, как в Kpi
  negativeText: '#dc2626', // red-600
  status: STATUS_COLORS,
  fontFamily: FONT_FAMILY,
}

export const DARK_THEME: ChartTheme = {
  mode: 'dark',
  surface: '#18181b', // zinc-900
  text: '#f4f4f5', // zinc-100
  textSecondary: '#d4d4d8', // zinc-300
  textMuted: '#a1a1aa', // zinc-400
  grid: '#27272a', // zinc-800
  axis: '#3f3f46', // zinc-700
  track: '#27272a',
  tooltipBackground: '#27272a',
  tooltipBorder: '#3f3f46',
  categorical: CATEGORICAL_DARK,
  other: '#71717a', // zinc-500
  sequential: SEQUENTIAL_DARK,
  diverging: DIVERGING_DARK,
  ordinal: ORDINAL_DARK,
  positiveText: '#4ade80', // green-400
  negativeText: '#f87171', // red-400
  status: STATUS_COLORS,
  fontFamily: FONT_FAMILY,
}

export function getChartTheme(mode: ChartMode): ChartTheme {
  return mode === 'dark' ? DARK_THEME : LIGHT_THEME
}

/** Цвет слота 0..7; всё за пределами палитры — «Прочее». Слоты не зацикливаются. */
export function seriesColor(theme: ChartTheme, slot: number | null | undefined): string {
  if (slot === null || slot === undefined || !Number.isInteger(slot) || slot < 0 || slot >= theme.categorical.length) {
    return theme.other
  }
  return theme.categorical[slot]
}

/** Цвет площадки — всегда один и тот же, при любом составе рядов. */
export function marketplaceColor(theme: ChartTheme, marketplace: string | null | undefined): string {
  const key = String(marketplace ?? '').toLowerCase()
  const normalized = key.startsWith('w') ? 'wb' : key.startsWith('o') ? 'ozon' : key.startsWith('y') ? 'yandex_market' : key
  return seriesColor(theme, ENTITY_SLOTS.marketplace[normalized])
}

/**
 * Закрепляет слоты за ключами сущностей по стабильному списку всех известных ключей (например, кабинеты из
 * справочника). Порядок списка — порядок слотов; фильтр, убравший часть рядов, не перекрашивает оставшиеся.
 * Ключи после восьмого — `null` («Прочее»).
 */
export function assignSlots(knownKeys: readonly string[]): Map<string, number | null> {
  const result = new Map<string, number | null>()
  knownKeys.forEach((key, index) => {
    if (!result.has(key)) result.set(key, result.size < 8 ? result.size : null)
  })
  return result
}

// ── Цвет на непрерывной шкале ─────────────────────────────────────────────────

type Rgb = [number, number, number]

function hexToRgb(hex: string): Rgb {
  const value = hex.replace('#', '')
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value
  const n = parseInt(full, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function rgbToHex([r, g, b]: Rgb): string {
  const part = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')
  return `#${part(r)}${part(g)}${part(b)}`
}

const toLinear = (c: number) => {
  const v = c / 255
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
}
const fromLinear = (v: number) => 255 * (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055)

/** sRGB → OKLab: интерполяция в нём не даёт грязной середины между полюсами. */
function rgbToOklab([r, g, b]: Rgb): Rgb {
  const lr = toLinear(r)
  const lg = toLinear(g)
  const lb = toLinear(b)
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

function oklabToRgb([L, a, b]: Rgb): Rgb {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    fromLinear(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    fromLinear(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    fromLinear(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ]
}

/** Цвет между `a` и `b` (t от 0 до 1), интерполяция в OKLab. */
export function mixColors(a: string, b: string, t: number): string {
  const k = Math.min(1, Math.max(0, t))
  // Концы — ровно исходные цвета (без погрешности перевода туда и обратно).
  if (k === 0) return a.toLowerCase()
  if (k === 1) return b.toLowerCase()
  const la = rgbToOklab(hexToRgb(a))
  const lb = rgbToOklab(hexToRgb(b))
  return rgbToHex(oklabToRgb([la[0] + (lb[0] - la[0]) * k, la[1] + (lb[1] - la[1]) * k, la[2] + (lb[2] - la[2]) * k]))
}

/** Цвет на ступенчатой шкале: `t` от 0 до 1 проходит все ступени по порядку. */
export function rampColor(ramp: readonly string[], t: number): string {
  if (ramp.length === 0) return '#000000'
  if (ramp.length === 1 || !Number.isFinite(t)) return ramp[0]
  const k = Math.min(1, Math.max(0, t)) * (ramp.length - 1)
  const index = Math.min(ramp.length - 2, Math.floor(k))
  return mixColors(ramp[index], ramp[index + 1], k - index)
}

/** Последовательная шкала: значение в пределах [min, max] → цвет. */
export function sequentialColor(theme: ChartTheme, value: number, min: number, max: number): string {
  const span = max - min
  return rampColor(theme.sequential, span > 0 ? (value - min) / span : 1)
}

/**
 * Расходящаяся шкала: значение в пределах [−limit, +limit] → цвет; ноль — серая середина.
 * Значения за пределами обрезаются по краю (выброс не «съедает» шкалу).
 */
export function divergingColor(theme: ChartTheme, value: number, limit: number): string {
  const [negative, middle, positive] = theme.diverging
  if (!(limit > 0)) return middle
  const t = Math.max(-1, Math.min(1, value / limit))
  return t < 0 ? mixColors(middle, negative, -t) : mixColors(middle, positive, t)
}

/** Относительная яркость (WCAG). */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Цвет подписи внутри залитой метки: белый или тёмный — что контрастнее к заливке. */
export function labelColorOn(fill: string, theme: ChartTheme): string {
  const l = relativeLuminance(fill)
  const contrastWhite = 1.05 / (l + 0.05)
  const contrastDark = (l + 0.05) / (relativeLuminance('#111827') + 0.05)
  return contrastWhite >= contrastDark ? '#ffffff' : theme.mode === 'dark' ? '#18181b' : '#111827'
}

/** Тот же цвет с прозрачностью (для заливки области 10% и ряда сравнения). */
export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${Math.min(1, Math.max(0, alpha))})`
}
